import {
  Collapse,
  InteractiveContainer,
  PlainButton,
} from "@jugl-web/ui-components";
import { cx, useAppVariant, useToast, useTranslations } from "@jugl-web/utils";
import React, { useMemo, useState } from "react";
import { ReactComponent as ChevronIcon } from "./assets/chevron.svg";
import { ReactComponent as CopyIcon } from "./assets/copy.svg";
import step1Image from "./assets/step-1.png";
import step2Image from "./assets/step-2.png";
import step3Image from "./assets/step-3.png";
import step4Image from "./assets/step-4.png";
import step5Image from "./assets/step-5.png";
import step6Image from "./assets/step-6.png";
import step7Image from "./assets/step-7.png";
import step8Image from "./assets/step-8.png";
import step9Image from "./assets/step-9.png";
import step10Image from "./assets/step-10.png";

// Scopes string the user must paste into Shopify's "Access Scopes" field during
// step 5 of the install flow. Kept verbatim from the Shopify Dev Dashboard spec
// the design references; update here if the backend ever requires a different
// set.
const SHOPIFY_ACCESS_SCOPES =
  "read_orders,read_products,unauthenticated_write_checkouts,unauthenticated_read_product_inventory,unauthenticated_read_product_listings,unauthenticated_read_content";

export const NeedHelpAccordion: React.FC = () => {
  const { t } = useTranslations();
  const { variant } = useAppVariant();
  const { toast } = useToast({ variant });
  const [isOpen, setIsOpen] = useState(true);

  const appUrl = `${window.location.origin}/connections/shopify`;

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast(t({ id: "common.copied", defaultMessage: "Copied" }));
    } catch {
      toast(
        t({
          id: "common.copy-failed",
          defaultMessage: "Failed to copy. Please copy manually.",
        }),
        { variant: "error" }
      );
    }
  };

  const steps: {
    id: number;
    title: string;
    description: string;
    image: string;
  }[] = useMemo(
    () => [
      {
        id: 1,
        title: t({
          id: "cx-actions-page.shopify-step-1-title",
          defaultMessage: "Login into Shopify dev dashboard",
        }),
        description: t({
          id: "cx-actions-page.shopify-step-1-description",
          defaultMessage: 'Click the "</>" icon to goto Shopify Dev Dashboard',
        }),
        image: step1Image,
      },
      {
        id: 2,
        title: t({
          id: "cx-actions-page.shopify-step-2-title",
          defaultMessage: "Create New App",
        }),
        description: t({
          id: "cx-actions-page.shopify-step-2-description",
          defaultMessage: 'Click "Create app" button to create new Shopify App',
        }),
        image: step2Image,
      },
      {
        id: 3,
        title: t({
          id: "cx-actions-page.shopify-step-3-title",
          defaultMessage: "Name the App (Jugl integration)",
        }),
        description: t({
          id: "cx-actions-page.shopify-step-3-description",
          defaultMessage: 'Click "Create" button to complete creation',
        }),
        image: step3Image,
      },
      {
        id: 4,
        title: t({
          id: "cx-actions-page.shopify-step-4-title",
          defaultMessage: 'Enter the following URL inside "App URL" input box',
        }),
        description: "",
        image: step4Image,
      },
      {
        id: 5,
        title: t({
          id: "cx-actions-page.shopify-step-5-title",
          defaultMessage:
            'Enter the following scopes inside "Access" Scopes input box',
        }),
        description: "",
        image: step5Image,
      },
      {
        id: 6,
        title: t({
          id: "cx-actions-page.shopify-step-6-title",
          defaultMessage: "Release new version",
        }),
        description: t({
          id: "cx-actions-page.shopify-step-6-description",
          defaultMessage:
            '"Release" button to create a new version. Enter version name and message optionally',
        }),
        image: step6Image,
      },
      {
        id: 7,
        title: t({
          id: "cx-actions-page.shopify-step-7-title",
          defaultMessage: "Go to Overview tab",
        }),
        description: t({
          id: "cx-actions-page.shopify-step-7-description",
          defaultMessage:
            "Once App and Version is created, click on the App Name (Jugl Integration)",
        }),
        image: step7Image,
      },
      {
        id: 8,
        title: t({
          id: "cx-actions-page.shopify-step-8-title",
          defaultMessage: "Copy the install Link",
        }),
        description: t({
          id: "cx-actions-page.shopify-step-8-description",
          defaultMessage:
            "Copy link and paste it inside Jugl “Instal Link” field",
        }),
        image: step8Image,
      },
      {
        id: 9,
        title: t({
          id: "cx-actions-page.shopify-step-9-title",
          defaultMessage: "Go to Settings tab",
        }),
        description: t({
          id: "cx-actions-page.shopify-step-9-description",
          defaultMessage: "The remaining details stored in the Settings tab",
        }),
        image: step9Image,
      },
      {
        id: 10,
        title: t({
          id: "cx-actions-page.shopify-step-10-title",
          defaultMessage: "Final step - Copy Client ID & Secret",
        }),
        description: t({
          id: "cx-actions-page.shopify-step-10-description",
          defaultMessage: "Copy this details and paste it inside Jugl",
        }),
        image: step10Image,
      },
    ],
    [t]
  );

  return (
    <div>
      <InteractiveContainer
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-start justify-between gap-3 py-2"
      >
        <div className="flex items-start gap-3">
          <div className="flex flex-col gap-0.5 text-left">
            <span className="font-secondary text-sm font-medium text-dark">
              <span className="mr-1">ℹ️</span>
              {t({
                id: "cx-actions-page.shopify-need-help",
                defaultMessage: "Need help?",
              })}
            </span>
            <span className="font-secondary text-xs leading-[140%] text-dark-600">
              {t({
                id: "cx-actions-page.shopify-need-help-description",
                defaultMessage:
                  "Check detailed instruction of how to get install link",
              })}
            </span>
          </div>
        </div>
        <ChevronIcon
          className={cx(
            "mt-1 shrink-0 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </InteractiveContainer>
      <Collapse isOpen={isOpen}>
        <div className="flex flex-col gap-6 pt-2 pb-5">
          {steps.map((step) => (
            <div key={step.id} className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-solid border-dark-200 font-secondary text-[11px] text-dark-600">
                  {step.id}
                </span>
                <span className="font-secondary text-sm font-medium text-dark">
                  {step.title}
                </span>
              </div>
              {step.description && (
                <span className="pl-7 font-secondary text-xs leading-[140%] text-dark-600">
                  {step.description}
                </span>
              )}
              {step.id === 4 && (
                <div className="pl-7">
                  <CopyPill value={appUrl} onCopy={handleCopy} />
                </div>
              )}
              {step.id === 5 && (
                <div className="pl-7">
                  <CopyPill value={SHOPIFY_ACCESS_SCOPES} onCopy={handleCopy} />
                </div>
              )}
              <img
                src={step.image}
                alt={step.title}
                className="ml-7 w-[calc(100%-1.75rem)] rounded-lg"
              />
            </div>
          ))}
        </div>
      </Collapse>
    </div>
  );
};

const CopyPill: React.FC<{
  value: string;
  onCopy: (value: string) => void;
}> = ({ value, onCopy }) => (
  <div className="flex items-center gap-2 rounded-lg border border-solid border-dark-100 px-3 py-2">
    <span className="min-w-0 flex-1 truncate font-secondary text-xs text-dark">
      {value}
    </span>
    <PlainButton
      onClick={() => onCopy(value)}
      className="flex shrink-0 items-center justify-center"
    >
      <CopyIcon />
    </PlainButton>
  </div>
);
